import {useEffect, useRef, useState} from "react"

import {
  API_BASE_URL,
  ENABLE_SSE_MESSAGE_STREAM,
  ENABLE_SSE_TOOL_BRIDGE,
} from "@frontend/env"
import {useTranslation} from "@frontend/i18n"
import {
  getHostPartPrices,
  toPartPriceRequestItem,
} from "@frontend/utils/appHostFunctions"
import type {SseStreamEvent} from "@frontend/utils/parseSse"
import {SSE_EVENT_TYPE, parseSseEvents} from "@frontend/utils/parseSse"
import {parseStream} from "@frontend/utils/parseStream"
import {invalidateCasePageQueries, trpc} from "@frontend/utils/trpc"
import {useTick} from "@frontend/utils/useTick"
import type {AddHumanMessageToCaseParams} from "@ri2/app/app/api/addHumanMessageToCase/route"
import {
  Case,
  Message,
  MessageImage,
  getCausesFromMessages,
} from "@ri2/db/client"

const DISABLE_OPTIMISTIC = false
const ENABLE_SSE_STREAM_TRANSPORT =
  ENABLE_SSE_MESSAGE_STREAM || ENABLE_SSE_TOOL_BRIDGE

console.log("[SSE] flags:", {
  ENABLE_SSE_MESSAGE_STREAM,
  ENABLE_SSE_TOOL_BRIDGE,
  ENABLE_SSE_STREAM_TRANSPORT,
})

export const OPTIMISTIC_HUMAN_MESSAGE_ID = "optimistic-human-message"
export const OPTIMISTIC_AI_MESSAGE_ID = "optimistic-ai-message"

const MESSAGE_ERROR_INDICATOR = "\u2302"

export const useSendMessage = (
  userRcId: string,
  caseModel: Case,
): {
  sendMessage: (
    message: string,
    images?: MessageImage[],
    starterPayload?: Ri2StarterBootstrapPayload | null,
  ) => void
  abortSendMessage: () => void
  isReceivingMessage: boolean
  receivingMessageStatus: string | null
} => {
  const t = useTranslation()

  const messageIds = caseModel.messages.map(({id}) => id)
  const isReceivingMessage =
    messageIds.includes(OPTIMISTIC_HUMAN_MESSAGE_ID) ||
    messageIds.includes(OPTIMISTIC_AI_MESSAGE_ID)

  const [timeOfLastStatusUpdate, setTimeOfLastStatusUpdate] = useState<
    number | null
  >(null)
  const [receivingMessageStatus, internalSetReceivingMessageStatus] = useState<
    string | null
  >(null)

  const setReceivingMessageStatus = (newStatus: string | null): void => {
    if (newStatus) {
      internalSetReceivingMessageStatus(newStatus)
      setTimeOfLastStatusUpdate(getNow())
    } else {
      internalSetReceivingMessageStatus(newStatus)
      setTimeOfLastStatusUpdate(null)
    }
  }

  useTick(!!receivingMessageStatus)

  const lastMessageIdRef = useRef<string | null>(null)

  const utils = trpc.useUtils()

  const [errored, setErrored] = useState(false)

  // If an error was thrown in the sendMessage promise, throw it to
  // the error boundary.
  if (errored) {
    throw new Error("Error sending message")
  }

  const [{abortSendMessage}, setAbortSendMessage] = useState({
    abortSendMessage: () => {},
  })

  const sendMessage = async (
    message: string,
    images?: MessageImage[],
    starterPayload?: Ri2StarterBootstrapPayload | null,
  ): Promise<void> => {
    if (isReceivingMessage) {
      console.error("Tried to send a message while receiving a message")
      setErrored(true)
      return
    }

    const lastMessageId = caseModel.messages[caseModel.messages.length - 1]?.id
    lastMessageIdRef.current = lastMessageId

    setReceivingMessageStatus(t("cases.case.analyzingMessage"))

    const optimisticHumanMessage: Message = {
      id: OPTIMISTIC_HUMAN_MESSAGE_ID,
      caseId: caseModel.id,
      createdAt: new Date(),
      type: "human",
      mode: null,
      basedOnMessageId: null,
      content: {
        content: message,
        images,
        answers: new Map(),
        isMultipleChoiceAnswer: false,
      },
    }

    const optimisticAiMessage: Message = {
      id: OPTIMISTIC_AI_MESSAGE_ID,
      caseId: caseModel.id,
      mode: null,
      basedOnMessageId: null,
      // date slightly in the future to guarantee it sorts after the
      // optimistic human message
      createdAt: new Date(Date.now() + 1),
      type: "ai",
      content: {question: ""},
      causes: getCausesFromMessages(caseModel.messages),
    }

    utils.casePageData.setData({id: caseModel.id}, (cached) => {
      if (!cached) return cached

      const {caseModel, ...old} = cached

      const messages = [...caseModel.messages, optimisticHumanMessage]

      if (!DISABLE_OPTIMISTIC) {
        messages.push(optimisticAiMessage)
      }

      return {
        ...old,
        caseModel: {
          ...caseModel,
          messages,
        },
      }
    })

    const abortController = new AbortController()

    setAbortSendMessage({
      abortSendMessage: () => {
        // `abort()` must be called with `abortController` as `this`
        abortController.abort()
      },
    })

    const params: AddHumanMessageToCaseParams = {
      userRcId,
      caseId: caseModel.id,
      message,
      images,
      pageContext: window.ri2?.pageContext ?? undefined,
      hostPartDataByOemNumber:
        starterPayload?.compatibleParts?.length &&
        starterPayload.compatibleParts.length > 0
          ? undefined
          : await getHostPartDataFromPageContext(),
      starterPayload: starterPayload ?? undefined,
      disableOptimistic: DISABLE_OPTIMISTIC,
    }

    const reload = (): void => {
      invalidateCasePageQueries(utils, {
        userRcId,
        caseId: caseModel.id,
      })
    }

    const setOptimisticAiQuestion = (question: string): void => {
      if (!question) return
      utils.casePageData.setData({id: caseModel.id}, (cached) => {
        if (!cached) return cached
        const {caseModel, ...old} = cached
        const messages = caseModel.messages.map((message) => {
          if (
            message.id === OPTIMISTIC_AI_MESSAGE_ID &&
            message.type === "ai"
          ) {
            return {
              ...message,
              content: {
                ...message.content,
                question,
              },
            }
          }
          return message
        })
        return {
          ...old,
          caseModel: {
            ...caseModel,
            messages,
          },
        }
      })
    }

    const appendToOptimisticAiQuestion = (text: string): void => {
      if (!text) return
      utils.casePageData.setData({id: caseModel.id}, (cached) => {
        if (!cached) return cached
        const {caseModel, ...old} = cached
        const messages = caseModel.messages.map((message) => {
          if (
            message.id === OPTIMISTIC_AI_MESSAGE_ID &&
            message.type === "ai"
          ) {
            return {
              ...message,
              content: {
                ...message.content,
                question: (message.content.question ?? "") + text,
              },
            }
          }
          return message
        })
        return {
          ...old,
          caseModel: {
            ...caseModel,
            messages,
          },
        }
      })
    }

    try {
      const response = await fetch(`${API_BASE_URL}/addHumanMessageToCase`, {
        method: "POST",
        headers: {
          "Accept-Encoding": "identity",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
        signal: abortController.signal,
      })

      if (!response.body) {
        throw new Error("No response body available for streaming")
      }

      const reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let legacyStream = ""
      let sseBuffer = ""

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const {done, value} = await reader.read()

        if (done) {
          console.log(
            "[SSE] stream done, flushing buffer, length:",
            sseBuffer.length,
          )
          if (ENABLE_SSE_STREAM_TRANSPORT && sseBuffer) {
            const {events} = parseSseEvents(`${sseBuffer}\n\n`)
            console.log("[SSE] flush parsed", events.length, "events")
            for (const event of events) {
              if (
                event.type === SSE_EVENT_TYPE.status &&
                typeof event.status === "string"
              ) {
                setReceivingMessageStatus(event.status)
              }
              if (
                event.type === SSE_EVENT_TYPE.token &&
                typeof event.text === "string"
              ) {
                appendToOptimisticAiQuestion(event.text)
              }
              if (
                event.type === SSE_EVENT_TYPE.final &&
                typeof event.message === "string"
              ) {
                setOptimisticAiQuestion(event.message)
              }
              if (event.type === SSE_EVENT_TYPE.hostToolCall) {
                await resolveHostToolCall(event)
              }
            }
          }
          reload()
          break
        }

        const chunk = decoder.decode(value)

        if (ENABLE_SSE_STREAM_TRANSPORT) {
          sseBuffer += chunk
          const {events, remainder} = parseSseEvents(sseBuffer)
          sseBuffer = remainder

          for (const event of events) {
            console.log("[SSE] parsed event:", event.type, event)

            if (
              event.type === SSE_EVENT_TYPE.status &&
              typeof event.status === "string"
            ) {
              setReceivingMessageStatus(event.status)
              continue
            }

            if (
              event.type === SSE_EVENT_TYPE.token &&
              typeof event.text === "string"
            ) {
              appendToOptimisticAiQuestion(event.text)
              continue
            }

            if (
              event.type === SSE_EVENT_TYPE.final &&
              typeof event.message === "string"
            ) {
              setOptimisticAiQuestion(event.message)
              continue
            }

            if (event.type === SSE_EVENT_TYPE.hostToolCall) {
              console.log(
                "[SSE] hostToolCall matched, calling resolveHostToolCall",
                event,
              )
              await resolveHostToolCall(event)
              continue
            }

            if (event.type === SSE_EVENT_TYPE.error) {
              throw new Error("Received stream error from backend")
            }

            console.log("[SSE] unhandled event type:", event.type)
          }
        } else {
          console.log(
            "[SSE] legacy path (SSE transport disabled), chunk length:",
            chunk.length,
          )
          if (chunk.includes(MESSAGE_ERROR_INDICATOR)) {
            throw new Error("Received error indicator from backend")
          }

          legacyStream += chunk
          const pendingStatus = parseStream(legacyStream)
          if (pendingStatus && pendingStatus !== receivingMessageStatus) {
            setReceivingMessageStatus(pendingStatus)
          }
        }
      }

      reader.cancel()
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        console.warn("Got abort error, presumably because user aborted", error)
      } else {
        console.error("Error sending message", error)

        setErrored(true)
      }
    } finally {
      if (lastMessageId === lastMessageIdRef.current) {
        reload()
      }
    }
  }

  useEffect(() => {
    if (receivingMessageStatus && !isReceivingMessage) {
      setReceivingMessageStatus(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReceivingMessage])

  const numSecondsBeforeStale = 10

  const isStatusStale =
    timeOfLastStatusUpdate &&
    getNow() - timeOfLastStatusUpdate > numSecondsBeforeStale

  const upToDateStatus =
    receivingMessageStatus && isStatusStale
      ? t("conversation.stillStatus", {
          status: receivingMessageStatus.toLowerCase(),
        })
      : receivingMessageStatus

  return {
    sendMessage,
    abortSendMessage,
    isReceivingMessage,
    receivingMessageStatus: upToDateStatus,
  }
}

const getNow = (): number => Math.floor(Date.now() / 1000)

const getHostPartDataFromPageContext = async (): Promise<
  Record<string, Ri2PartPriceResponseValue> | undefined
> => {
  const contextPartNumber = window.ri2?.pageContext?.partNumber?.trim()
  if (!contextPartNumber) return undefined

  const manufacturerName = ""
  const requestItems = [contextPartNumber].map((partNumber) =>
    toPartPriceRequestItem({
      oem_number: partNumber,
      manufacturer_name: manufacturerName,
    }),
  )

  const hostData = await getHostPartPrices(requestItems)
  return Object.keys(hostData).length > 0 ? hostData : undefined
}

const resolveHostToolCall = async (event: SseStreamEvent): Promise<void> => {
  const callId = typeof event.callId === "string" ? event.callId : null
  const name = typeof event.name === "string" ? event.name : null
  const args = event.args

  console.log("[resolveHostToolCall] start", {callId, name, args})

  if (!callId || !name || !args || typeof args !== "object") {
    console.warn("[resolveHostToolCall] bail — missing callId/name/args", {
      callId,
      name,
      args,
    })
    return
  }

  const payload: {callId: string; result?: unknown; error?: string} = {callId}

  try {
    if (name === "fetchPartsByOemAndManufacturer") {
      const partNumberRaw = (args as {partNumber?: unknown}).partNumber
      const manufacturerRaw = (args as {manufacturer?: unknown}).manufacturer
      const partNumber =
        typeof partNumberRaw === "string"
          ? partNumberRaw.trim().toUpperCase()
          : ""
      const manufacturer =
        typeof manufacturerRaw === "string"
          ? manufacturerRaw.trim() || undefined
          : undefined

      if (!partNumber) {
        payload.error = "Missing partNumber"
      } else {
        const fetchPartsByOemAndManufacturer =
          window.ri2?.fetchPartsByOemAndManufacturer
        if (fetchPartsByOemAndManufacturer) {
          const parts = await fetchPartsByOemAndManufacturer(
            partNumber,
            manufacturer,
          )
          payload.result = Array.isArray(parts) ? parts : []
        } else {
          payload.result = []
        }
      }
    } else {
      payload.error = `Unknown host tool: ${name}`
    }
  } catch (error) {
    console.error("[resolveHostToolCall] error", error)
    payload.error =
      error instanceof Error ? error.message : "Host tool execution failed"
  }

  console.log("[resolveHostToolCall] posting to backend", payload)
  const resp = await fetch(`${API_BASE_URL}/resolveHostToolCall`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })
  console.log(
    "[resolveHostToolCall] backend response",
    resp.status,
    await resp.json().catch(() => null),
  )
}
